/**
 * Catálogo de herramientas.
 *
 * Cada entrada define además su URL y sus metadatos de SEO. El sitemap
 * y el prerenderizado se generan a partir de este archivo, así que al
 * añadir una herramienta aquí queda automáticamente indexable.
 */

export const TOOLS = [
  {
    popular: true,
    id: "tool_iva",
    slug: "calculadora-de-iva",
    name: "Calculadora de IVA",
    shortDescription: "Agrega o extrae el IVA fácilmente.",
    description:
      "Suma o desglosa porcentajes de IVA para México y otras regiones de América Latina y Europa con total precisión.",
    category: "Finanzas",
    icon: "Percent",
    seo: {
      title: "Calculadora de IVA 2026 — Sumar y desglosar IVA en México",
      description:
        "Calcula el IVA al 16% u 8% de frontera: suma el impuesto a un subtotal o desglósalo de un precio con IVA incluido. Gratis y sin registro.",
      h1: "Calculadora de IVA",
      keywords: ["calculadora de iva", "desglosar iva", "iva 16", "quitar iva", "iva frontera 8"],
    },
  },
  {
    popular: true,
    id: "tool_infonavit",
    slug: "simulador-credito-infonavit",
    name: "Crédito INFONAVIT",
    shortDescription: "Simulador de amortización y pagos extra.",
    description:
      "Calcula tu ahorro de intereses y de meses de deuda al realizar abonos anuales voluntarios a tu crédito de vivienda.",
    category: "Finanzas",
    icon: "Building2",
    seo: {
      title: "Simulador de Crédito INFONAVIT 2026 — Amortización y pagos extra",
      description:
        "Simula tu crédito INFONAVIT: tabla de amortización, tasa según tu sueldo y cuánto ahorras en intereses y años con abonos voluntarios.",
      h1: "Simulador de Crédito INFONAVIT",
      keywords: ["simulador infonavit", "credito infonavit", "amortizacion infonavit", "pagos extra infonavit"],
    },
  },
  {
    popular: true,
    id: "tool_sueldo",
    slug: "calculadora-sueldo-neto-imss-isr",
    name: "Sueldo Neto IMSS / ISR",
    shortDescription: "Sueldo Diario Integrado (SBC) y retenciones 2026.",
    description:
      "Estima tu sueldo real restando cuotas de seguridad social del IMSS y retenciones progresivas de impuestos del SAT.",
    category: "Finanzas",
    icon: "FileSpreadsheet",
    seo: {
      title: "Calculadora de Sueldo Neto 2026 — ISR, IMSS y SBC en México",
      description:
        "Calcula tu sueldo neto mensual: retención de ISR con la tarifa del SAT, cuotas obrero del IMSS, subsidio al empleo y Salario Base de Cotización.",
      h1: "Calculadora de Sueldo Neto (IMSS e ISR)",
      keywords: ["sueldo neto", "calculadora isr", "cuotas imss", "salario base de cotizacion", "sbc"],
    },
  },
  {
    id: "tool_interes",
    slug: "calculadora-interes-compuesto",
    name: "Simple y Compuesto",
    shortDescription: "Calcula el crecimiento de tu dinero.",
    description:
      "Compara proyecciones financieras evaluando intereses periódicos y reinversiones con cronograma de saldos capitalizados.",
    category: "Finanzas",
    icon: "TrendingUp",
    seo: {
      title: "Calculadora de Interés Compuesto y Simple — Proyecta tu ahorro",
      description:
        "Proyecta cuánto crece tu dinero con interés simple o compuesto, con aportaciones periódicas y tabla de saldos año por año.",
      h1: "Calculadora de Interés Simple y Compuesto",
      keywords: ["interes compuesto", "calculadora de interes", "interes simple", "proyeccion de ahorro"],
    },
  },
  {
    popular: true,
    id: "tool_divisa",
    slug: "conversor-de-divisas",
    name: "Conversor de Divisas",
    shortDescription: "Tipos de cambio al día.",
    description:
      "Convierte montos comerciales entre pesos mexicanos (MXN), dólares (USD), euros (EUR), libras (GBP) y más al instante.",
    category: "Conversores",
    icon: "Coins",
    seo: {
      title: "Conversor de Divisas — Dólar, Euro y Peso Mexicano hoy",
      description:
        "Convierte entre peso mexicano, dólar, euro, libra y más con tipos de cambio actualizados. Rápido, gratis y sin registro.",
      h1: "Conversor de Divisas",
      keywords: ["conversor de divisas", "dolar a peso", "tipo de cambio", "euro a peso mexicano"],
    },
  },
  {
    id: "tool_medidas",
    slug: "conversor-de-medidas",
    name: "Conversor de Medidas",
    shortDescription: "Unidades métricas e imperiales.",
    description:
      "Convierte longitud, masa, volumen, área y temperatura con factores de equivalencia universales precisos.",
    category: "Conversores",
    icon: "Ruler",
    seo: {
      title: "Conversor de Medidas — Longitud, peso, volumen y temperatura",
      description:
        "Convierte entre unidades métricas e imperiales: metros a pies, kilos a libras, litros a galones, Celsius a Fahrenheit y más.",
      h1: "Conversor de Medidas",
      keywords: ["conversor de medidas", "kilos a libras", "metros a pies", "celsius a fahrenheit"],
    },
  },
  {
    id: "tool_porcentaje",
    slug: "calculadora-de-porcentajes",
    name: "Calculadora de Porcentajes",
    shortDescription: "Tres operaciones de porcentajes rápidas.",
    description:
      "Halla proporciones, calcula el valor exacto de cualquier tasa o determina el porcentaje de aumento o descenso.",
    category: "Finanzas",
    icon: "Percent",
    seo: {
      title: "Calculadora de Porcentajes — Cuánto es el X% de una cantidad",
      description:
        "Calcula el porcentaje de una cantidad, qué porcentaje representa un número sobre otro y la variación porcentual entre dos valores.",
      h1: "Calculadora de Porcentajes",
      keywords: ["calculadora de porcentajes", "sacar porcentaje", "variacion porcentual", "regla de tres"],
    },
  },
  {
    id: "tool_descuentos",
    slug: "calculadora-de-descuentos",
    name: "Descuentos y Unitario",
    shortDescription: "Ahorro apilado y volumen.",
    description:
      "Encuentra el precio final con rebajas acumuladas y compara ofertas de súper para ver el costo unitario real.",
    category: "Finanzas",
    icon: "Tag",
    seo: {
      title: "Calculadora de Descuentos — Precio final y comparador unitario",
      description:
        "Calcula el precio final con descuentos acumulados y compara cuál oferta del súper conviene según su costo por unidad.",
      h1: "Calculadora de Descuentos y Precio Unitario",
      keywords: ["calculadora de descuentos", "precio con descuento", "precio unitario", "descuentos acumulados"],
    },
  },
  {
    id: "tool_presupuesto",
    slug: "presupuesto-50-30-20",
    name: "Presupuesto 50/30/20",
    shortDescription: "Ordena tus finanzas mensuales.",
    description:
      "Compara tus gastos fijos, flexibles y capacidad de capitalización con la sugerencia saludable de expertos.",
    category: "Finanzas",
    icon: "PiggyBank",
    seo: {
      title: "Presupuesto 50/30/20 — Calculadora de finanzas personales",
      description:
        "Reparte tu ingreso mensual con la regla 50/30/20 y compara tus gastos reales contra el reparto recomendado.",
      h1: "Calculadora de Presupuesto 50/30/20",
      keywords: ["presupuesto 50 30 20", "finanzas personales", "regla 50 30 20", "presupuesto mensual"],
    },
  },
  {
    id: "tool_negocio",
    slug: "calculadora-margen-roi-punto-equilibrio",
    name: "Margen, ROI y Equilibrio",
    shortDescription: "Métricas de éxito comercial.",
    description:
      "Obtén el porcentaje de utilidad de tus productos, la velocidad de retorno de inversión y tu volumen de punto de equilibrio.",
    category: "Negocios",
    icon: "Briefcase",
    seo: {
      title: "Calculadora de Margen de Ganancia, ROI y Punto de Equilibrio",
      description:
        "Calcula el margen de utilidad de tus productos, el retorno sobre la inversión y cuántas unidades necesitas vender para no perder.",
      h1: "Margen de Ganancia, ROI y Punto de Equilibrio",
      keywords: ["margen de ganancia", "calculadora roi", "punto de equilibrio", "markup"],
    },
  },
  {
    id: "tool_freelance",
    slug: "calculadora-tarifa-freelance",
    name: "Tarifa Freelance",
    shortDescription: "Establece tu precio hora/día.",
    description:
      "Define cuánto cobrar considerando tu vacación ideal, impuestos, gastos operativos y el tiempo real facturable.",
    category: "Negocios",
    icon: "Laptop",
    seo: {
      title: "Calculadora de Tarifa Freelance — Cuánto cobrar por hora",
      description:
        "Calcula tu tarifa por hora y por día como freelance a partir del ingreso neto que quieres, tus gastos, impuestos y horas facturables reales.",
      h1: "Calculadora de Tarifa Freelance",
      keywords: ["tarifa freelance", "cuanto cobrar por hora", "precio por hora freelance", "tarifa por proyecto"],
    },
  },
  {
    id: "tool_imc",
    slug: "calculadora-imc",
    name: "Salud e IMC",
    shortDescription: "Índice de masa corporal y peso ideal.",
    description:
      "Calcula tu Índice de Masa Corporal de manera rápida y descubre el intervalo ideal recomendado para tu altura.",
    category: "Salud",
    icon: "Heart",
    seo: {
      title: "Calculadora de IMC — Índice de Masa Corporal y peso ideal",
      description:
        "Calcula tu índice de masa corporal (IMC) con tu peso y estatura, y consulta el rango de peso saludable para tu altura.",
      h1: "Calculadora de IMC (Índice de Masa Corporal)",
      keywords: ["calculadora imc", "indice de masa corporal", "peso ideal", "imc segun estatura"],
    },
  },
  {
    id: "tool_viaje",
    slug: "calculadora-costo-de-viaje",
    name: "Costo de Viaje y Ruta",
    shortDescription: "Gasolina, casetas y reparto por pasajero.",
    description:
      "Planea el total de litros requeridos para llegar a tu destino, peajes de la SCT y la cotización por pasajero.",
    category: "Rutas",
    icon: "Car",
    seo: {
      title: "Calculadora de Costo de Viaje — Gasolina, casetas y por pasajero",
      description:
        "Calcula cuánta gasolina gasta tu viaje, súmale casetas y divide el costo total entre los pasajeros. Ideal para viajes por carretera en México.",
      h1: "Calculadora de Costo de Viaje",
      keywords: ["costo de viaje", "calculadora de gasolina", "gasto de gasolina", "casetas mexico"],
    },
  },
  {
    popular: true,
    id: "tool_obra",
    slug: "calculadora-de-presupuesto-de-obra",
    name: "Presupuesto de obra",
    shortDescription: "Costo de construcción por m² con precios unitarios.",
    description:
      "Captura los metros de construcción y los precios unitarios (muro de block pegado, firme, losa, instalaciones) para obtener el costo total de la obra.",
    category: "Construcción",
    icon: "HardHat",
    seo: {
      title: "Calculadora de presupuesto de obra y costo por m² de construcción 2026",
      description:
        "Calcula el costo de construir una casa: metros cuadrados, precios unitarios de block, firme, losa e instalaciones, más desperdicio, indirectos, utilidad e IVA. Gratis.",
      h1: "Calculadora de presupuesto de obra",
      keywords: ["costo de construcción por m2", "presupuesto de obra", "precio unitario block pegado", "cuanto cuesta construir una casa", "calculadora de construcción"],
    },
  },
  {
    id: "tool_sugerir",
    slug: "proponer-herramienta",
    name: "💡 Proponer Nueva Herramienta",
    shortDescription: "Dinos qué calculadora te hace falta.",
    description:
      "Buzón directo: cuéntanos qué utilidad matemática o financiera necesitas de forma libre y transparente.",
    category: "Sugerencias",
    icon: "MessageSquare",
    seo: {
      title: "Proponer una herramienta — tu-kit.com",
      description: "¿Te falta una calculadora? Cuéntanos cuál necesitas y la construimos.",
      h1: "Propón una nueva herramienta",
      keywords: [],
      noindex: true,
    },
  },
];

export const POPULARES = TOOLS.filter((t) => t.popular);

export const CATEGORIES = [...new Set(TOOLS.map((t) => t.category))];

export const getToolBySlug = (slug) => TOOLS.find((t) => t.slug === slug);
export const getToolById = (id) => TOOLS.find((t) => t.id === id);

/** Rutas indexables, para el sitemap y el prerenderizado. */
export const INDEXABLE_ROUTES = [
  "/",
  ...TOOLS.filter((t) => !t.seo.noindex).map((t) => `/${t.slug}`),
  "/aviso-de-privacidad",
  "/terminos-y-condiciones",
  "/acerca-de",
  "/contacto",
];
